import { buildDocumentViewerQueryString } from "@/partials/document-renderer/utils/referenceHighlightQuery";

export type ReferenceHighlightRouteOptions = {
  matchText?: string;
  chunkText?: string;
};

/**
 * Viewer query options: the reference highlight plus `docSearch`, the term the
 * in-document search is seeded with when a global search result is opened.
 */
export type DocumentViewerRouteOptions = ReferenceHighlightRouteOptions & {
  docSearch?: string;
};

class AppRoutes {
  /* Index */
  static readonly index = "/";

  /* Auth */
  static readonly auth = "/auth";
  static readonly unauthorized = "/unauthorized";
  static readonly onboarding = "/onboarding";

  /* NavRoutes */
  static readonly dashboard = "/dashboard";

  //Chat
  static readonly chat = "/chat";
  /**
   * The open conversation, so a reload, a discarded tab or a re-login can put the
   * user back in it — it used to live only in memory (LAR-361). Single dynamic
   * segment, so it never competes with `/chat/legislation/...` or
   * `/chat/workspace/...`, which are longer and start with a static segment.
   */
  static readonly chatConversation = (conversationId = ":conversationId") =>
    `${this.chat}/${conversationId}`;
  static readonly workspaceChat = (id = ":workspaceId") =>
    `${this.chat}?workspaceId=${id}`;
  static readonly chatWorkspaceHTMLView = (
    workspaceId = ":workspaceId",
    blobName = ":blobName",
    htmlIds?: string[],
    refHighlight?: DocumentViewerRouteOptions
  ) => {
    const basePath = `${this.chat}/workspace/${workspaceId}/document/${blobName}`;
    const qs = buildDocumentViewerQueryString({
      htmlIds,
      matchText: refHighlight?.matchText,
      chunkText: refHighlight?.chunkText,
      docSearch: refHighlight?.docSearch,
    });
    return `${basePath}${qs}`;
  };
  static readonly chatLegislationHTMLView = (
    documentId = ":documentId",
    datasource = ":datasource",
    htmlIds?: string[],
    refHighlight?: DocumentViewerRouteOptions
  ) => {
    const basePath = `${this.chat}${this.legislations}/${documentId}/datasource/${datasource}`;
    const qs = buildDocumentViewerQueryString({
      htmlIds,
      matchText: refHighlight?.matchText,
      chunkText: refHighlight?.chunkText,
      docSearch: refHighlight?.docSearch,
    });
    return `${basePath}${qs}`;
  };
  static readonly chatLegislationPDFView = (documentId = ":documentId") =>
    `${this.chat}${this.legislations}/${documentId}`;
  static readonly chatWorkspacePDFView = (
    workspaceId = ":workspaceId",
    blobName = ":blobName",
    isExcel = ":isExcel"
  ) =>
    `${this.chat}/workspace/${workspaceId}/document/${blobName}/excel/${isExcel}`;

  static readonly prompts = "/prompts";
  //Legislation
  static readonly legislations = "/legislation";
  static readonly legislationHTML = (
    documentId = ":documentId",
    datasource = ":datasource",
    htmlIds?: string[],
    refHighlight?: DocumentViewerRouteOptions
  ) => {
    const basePath = `${this.legislations}/${documentId}/datasource/${datasource}`;
    const qs = buildDocumentViewerQueryString({
      htmlIds,
      matchText: refHighlight?.matchText,
      chunkText: refHighlight?.chunkText,
      docSearch: refHighlight?.docSearch,
    });
    return `${basePath}${qs}`;
  };
  static readonly legislationPDF = (
    documentId = ":documentId",
    datasource = ":datasource",
    elementId?: string,
    page?: string
  ) => {
    const basePath = `${this.legislations}/${documentId}/datasource/${datasource}/pdf`;
    if (elementId) {
      return `${basePath}?elementId=${elementId}`;
    }
    if (page) {
      return `${basePath}?page=${page}`;
    }
    return basePath;
  };

  /* AppRoutes */
  static readonly apps = "/apps";

  //Workspaces
  static readonly workspaces = `/workspaces`;
  static readonly workspace = (id = ":workspaceId") => `/workspaces/${id}`;
  static readonly workspaceFiles = (id = ":workspaceId") =>
    `/workspaces/${id}/files`;
  static readonly workspaceFilesFolder = (
    id = ":workspaceId",
    folderPath = ":folderPath*"
  ) => `/workspaces/${id}/files/${folderPath}`;
  static readonly workspaceComments = (id = ":workspaceId") =>
    `/workspaces/${id}/comments`;
  static readonly workspaceCommentsDocument = (
    id = ":workspaceId",
    documentId = ":documentId"
  ) => `/workspaces/${id}/comments/${documentId}`;
  static readonly workspaceSettings = (id = ":workspaceId") =>
    `/workspaces/${id}/settings`;
  static readonly workspaceDocumentRedirect = (
    id = ":workspaceId",
    documentId = ":documentId",
    htmlIds?: string[],
    refHighlight?: DocumentViewerRouteOptions
  ) => {
    const basePath = `/workspaces/${id}/document/${documentId}`;
    const qs = buildDocumentViewerQueryString({
      htmlIds,
      matchText: refHighlight?.matchText,
      chunkText: refHighlight?.chunkText,
      docSearch: refHighlight?.docSearch,
    });
    return `${basePath}${qs}`;
  };
  static readonly workspaceDocumentPDF = (
    id = ":workspaceId",
    blobName = ":blobName",
    type = ":type"
  ) => `/workspaces/${id}/document/${blobName}/type/${type}`;
  static readonly workspaceDocumentHTML = (
    id = ":workspaceId",
    blobName = ":blobName",
    htmlIds?: string[],
    refHighlight?: DocumentViewerRouteOptions
  ) => {
    const basePath = `/workspaces/${id}/document/${blobName}/html`;
    const qs = buildDocumentViewerQueryString({
      htmlIds,
      matchText: refHighlight?.matchText,
      chunkText: refHighlight?.chunkText,
      docSearch: refHighlight?.docSearch,
    });
    return `${basePath}${qs}`;
  };

  //Prompt Library
  static readonly promptLibrary = "/prompt-library";

  //Insights
  static readonly insights = "/insights/conversations";
  static readonly insightsUserOverview = (
    id = ":conversationId",
    isDeleted = ":isDeleted"
  ) => `/insights/user-overview/${id}/${isDeleted}`;
  static readonly insightsUsers = "/insights/users";

  //Settings
  static readonly settingsToneOfVoice = "/settings/tone-of-voice";
  static readonly settingsUserManagement = "/settings/user-management";
  static readonly settingsUserManagementUsers =
    "/settings/user-management/users";
  static readonly settingsUserManagementInvitedUsers =
    "/settings/user-management/invited-users";
  static readonly settingsUserManagementGroups =
    "/settings/user-management/groups";
  static readonly settingsUserManagementOrphanedWorkspaces =
    "/settings/user-management/orphaned-workspaces";
  static readonly settingsEntraDirectorySync = "/settings/entra-directory-sync";
  static readonly settingsFeatures = "/settings/features";
  static readonly settingsAutomaticDeletion = "/settings/automatic-deletion";
  static readonly settingsMetadataTypes = "/settings/metadata";
  static readonly settingsSynonyms = "/settings/synonyms";
  static readonly settingsSynonymDetail = (id = ":synonymGroupId") =>
    `/settings/synonyms/${id}`;
  static readonly settingsWidgetTemplates = "/settings/widget-templates";
  static readonly settingsPiiWhitelist = "/settings/pii-whitelist";
  static readonly settingsConsolidationPriority =
    "/settings/konsolideringsprioritering";

  //Tenant Search
  static readonly tenantSearch = "/search";

  // Document link redirect
  static readonly documentLinkRedirect = (documentLinkId = ":documentLinkId") =>
    `/docLink/${documentLinkId}`;

  /* Error */
  static readonly error = "/error";
  static readonly notFound = "/404";
  static readonly workspaceFileNotFound = "/404/workspace/file-not-found";
  static readonly workspaceFileNotFoundDashboard =
    "/404/workspace/file-not-found/dashboard";
  static readonly workspaceNotFound = "/404/workspace";
  static readonly legislationFileNotFound = "/404/legislation/file-not-found";
  static readonly legislationNotFound = "/404/legislation";
  static readonly chatFileNotFound = "/404/chat/file-not-found";
  static readonly insightsUserOverviewNotFound = "404/overview-not-found";
  static readonly accessDenied = "/403";
}

type RouteTitle = string | ((params: Record<string, string>) => string);

export const ROUTE_TITLES: Record<string, RouteTitle> = {
  // Static routes
  [AppRoutes.insights]: "Insights",
  [AppRoutes.promptLibrary]: "Promptbibliotek",
  [AppRoutes.insightsUsers]: "Bruger Insights",
  [AppRoutes.settingsToneOfVoice]: "Tone of Voice",
  [AppRoutes.settingsUserManagement]: "Brugerstyring",
  [AppRoutes.settingsEntraDirectorySync]: "Entra directory sync",
  [AppRoutes.settingsFeatures]: "Features",
  [AppRoutes.settingsAutomaticDeletion]: "Automatisk sletning",
  [AppRoutes.settingsMetadataTypes]: "Metadata",
  [AppRoutes.settingsWidgetTemplates]: "Widget-skabeloner",
  [AppRoutes.settingsPiiWhitelist]: "Godkendte ord",
  [AppRoutes.settingsConsolidationPriority]: "Konsolideringsprioritering",

  [AppRoutes.dashboard]: "Dashboard",
  [AppRoutes.chat]: "Chat",
  [AppRoutes.chatConversation()]: "Chat",
  [AppRoutes.legislations]: "Kilder",
  [AppRoutes.workspaces]: "Workspaces",
  [AppRoutes.tenantSearch]: "Søgning",
  // Dynamic routes with generic titles
  "/insights/user-overview/:conversationId/:isDeleted":
    "Bruger Samtale Oversigt",
  "/workspaces/:workspaceId": "Workspace Detaljer",
  "/workspaces/:workspaceId/files": "Workspace Filer",
  "/workspaces/:workspaceId/files/*": "Workspace Filer",
  "/workspaces/:workspaceId/comments": "Workspace Kommentarer",
  "/workspaces/:workspaceId/comments/:documentId": "Workspace Kommentarer",
  "/workspaces/:workspaceId/settings": "Workspace Indstillinger",
  "/workspaces/:workspaceId/document/:blobName": "Workspace Dokument",
  "/legislation/:documentId": "Kilde Detaljer",
  "/chat/pdf/:documentId": "Dokument Visning",
  "/chat/workspace/:workspaceId/pdf/:blobName": "Dokument Visning",
  "/settings/user-management/invited-users": "Inviterede brugere",
  "/settings/user-management/groups": "Grupper",
  "/settings/user-management/users": "Brugerstyring",
  "/settings/user-management/orphaned-workspaces": "Forældreløse workspaces",
  // Error routes
  [AppRoutes.notFound]: "Side Ikke Fundet",
  [AppRoutes.workspaceFileNotFound]: "Dokument Ikke Fundet",
  [AppRoutes.workspaceFileNotFoundDashboard]: "Dokument Ikke Fundet",
  [AppRoutes.workspaceNotFound]: "Workspace Ikke Fundet",
  [AppRoutes.legislationFileNotFound]: "Kildedokumentet blev ikke fundet",
  [AppRoutes.legislationNotFound]: "Kilden blev ikke fundet",
  [AppRoutes.chatFileNotFound]: "Dokument Ikke Fundet",
  [AppRoutes.insightsUserOverviewNotFound]:
    "Bruger Samtale Oversigt Ikke Fundet",
  [AppRoutes.accessDenied]: "Adgang Nægtet",
} as const;

export default AppRoutes;
